import { DoNotCallRestrictionType } from 'infrastructure/enums';
import { useMemo } from 'react';
import { isInDoNotCallWindow } from 'infrastructure/functions/is-in-do-not-call-window';

import type { ClinicDoNotCallRestrictionType } from 'infrastructure/enums';
import type { Nullable } from 'infrastructure/types';

interface ICanCallProps {
  doNotCall?: boolean;
  restrictionType?: Nullable<
    DoNotCallRestrictionType | ClinicDoNotCallRestrictionType
  >;
  startTime?: Nullable<string>;
  endTime?: Nullable<string>;
  patientTimezone?: Nullable<string>;
}

const canCall = ({
  restrictionType,
  startTime,
  endTime,
  patientTimezone,
}: ICanCallProps) => {
  if (restrictionType === DoNotCallRestrictionType.FullRestriction)
    return false;
  if (
    restrictionType === DoNotCallRestrictionType.LimitedHours &&
    startTime &&
    endTime
  ) {
    return !isInDoNotCallWindow(startTime, endTime, patientTimezone);
  }

  return true;
};

interface IProps {
  doNotCallRestrictionType?: Nullable<
    DoNotCallRestrictionType | ClinicDoNotCallRestrictionType
  >;
  ccmDoNotCallRestrictionType?: Nullable<
    DoNotCallRestrictionType | ClinicDoNotCallRestrictionType
  >;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  ccmDoNotCallStartTime?: Nullable<string>;
  ccmDoNotCallEndTime?: Nullable<string>;
  patientTimezone?: Nullable<string>;
}

export const useCallAccess = (props: IProps) => {
  const {
    doNotCallRestrictionType,
    ccmDoNotCallRestrictionType,
    doNotCallStartTime,
    doNotCallEndTime,
    ccmDoNotCallStartTime,
    ccmDoNotCallEndTime,
    patientTimezone,
  } = props;

  const canCallRpm = useMemo(
    () =>
      canCall({
        restrictionType: doNotCallRestrictionType,
        startTime: doNotCallStartTime,
        endTime: doNotCallEndTime,
        patientTimezone,
      }),
    [
      doNotCallRestrictionType,
      doNotCallStartTime,
      doNotCallEndTime,
      patientTimezone,
    ],
  );

  const canCallCcm = useMemo(
    () =>
      canCall({
        restrictionType: ccmDoNotCallRestrictionType,
        startTime: ccmDoNotCallStartTime,
        endTime: ccmDoNotCallEndTime,
        patientTimezone,
      }),
    [
      ccmDoNotCallRestrictionType,
      ccmDoNotCallStartTime,
      ccmDoNotCallEndTime,
      patientTimezone,
    ],
  );

  return {
    canCallRpm,
    canCallCcm,
  };
};
